import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Controller, useFormContext } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import cn from 'classnames';
dayjs.extend(utc);
dayjs.extend(timezone);

// Api
import {
  GET_ALL_AVAILABLE_SCHEDULE_SLOTS,
  GET_AVAILABLE_INTERVIEWERS,
  GET_AVAILABLE_SCHEDULE_SLOTS,
  GET_INTERVIEW,
} from 'api/interview/queries';
import { GET_INTERVIEW_AVAILABLE_SLOTS } from 'api/schedule/queries';
// Types
import {
  GetAvailableScheduleSlots,
  GetAvailableScheduleSlotsVariables,
} from 'api/interview/types/GetAvailableScheduleSlots';
import {
  getAvailableInterviewers,
  getAvailableInterviewersVariables,
} from 'api/interview/types/getAvailableInterviewers';
import {
  GetAllAvailableScheduleSlots,
  GetAllAvailableScheduleSlotsVariables,
} from 'api/interview/types/GetAllAvailableScheduleSlots';
import {
  GetInterviewAvailableSlots,
  GetInterviewAvailableSlotsVariables,
} from 'api/schedule/types/GetInterviewAvailableSlots';
import {
  GetInterview,
  GetInterviewVariables,
} from 'api/interview/types/GetInterview';
import { Interviewer } from 'components/UserScheduleInterview/helpers/localTypes';
// Helpers
import {
  getExcludedDates,
  getStartEndOfMonth,
} from 'helpers/scheduleInterview';
// Hooks
import { useCurrentMutableStateRef } from 'hooks';
// Ui
import Text from 'ui3/Text/Text';
import TextArea from 'ui3/TextArea/TextArea';
import DatePicker from 'ui3/DatePicker/DatePicker';
import Loader from 'ui3/Loader/Loader';
// Components
import { showToast } from 'components/common/Toast/Toast';
import { ScheduleInterviewFormValues } from 'components/UserScheduleInterview/ScheduleInterviewGuestModal/ScheduleInterviewGuestModal';
// Styles
import styles from './CalendarForm.module.scss';
import { InterviewSourceType } from 'api/graphql-global-types';

export interface CommonSlot {
  __typename: string;
  startTs: string;
  endTs: string;
  userId?: string | null;
}

type CalendarStepFormProps = {
  interviewId: string | null;
  hostId: string | null;
  loggedUserId: string | null;
  onInterviewerChange?: (interviewer: Interviewer) => void;
};

const CalendarStepForm = ({
  interviewId,
  hostId,
  loggedUserId,
  onInterviewerChange,
}: CalendarStepFormProps) => {
  const {
    control,
    errors,
    register,
    watch,
    setValue,
    setError,
  } = useFormContext<ScheduleInterviewFormValues>();

  // Check if screen is mobile (below breakpoint_m which is 768px)
  const isMobileScreen = useMediaQuery({ maxWidth: 767 });

  // Get interview data to access the source
  const { data: interviewData } = useQuery<GetInterview, GetInterviewVariables>(
    GET_INTERVIEW,
    {
      variables: {
        input: {
          id: interviewId as string,
        },
      },
      skip: !interviewId,
    }
  );

  const minCalendarDateRef = useRef(dayjs().startOf('day').toDate());
  const [filteredTimeSlots, setFilteredTimeSlots] = useState<CommonSlot[]>([]);
  const autoJumpedRef = useRef(false);

  const [
    getAvailableScheduleSlots,
    { data: scheduleSlotsData, loading: loadingAvailableScheduleSlots },
  ] = useLazyQuery<
    GetAvailableScheduleSlots,
    GetAvailableScheduleSlotsVariables
  >(GET_AVAILABLE_SCHEDULE_SLOTS, {
    fetchPolicy: 'cache-and-network',
  });

  const [
    getAllAvailableScheduleSlots,
    { data: allScheduleSlotsData, loading: loadingAllScheduleSlots },
  ] = useLazyQuery<
    GetAllAvailableScheduleSlots,
    GetAllAvailableScheduleSlotsVariables
  >(GET_ALL_AVAILABLE_SCHEDULE_SLOTS, {
    fetchPolicy: 'cache-and-network',
  });

  const [
    getInterviewAvailableSlots,
    { data: interviewAvailableSlotsData, loading: loadingInterviewSlots },
  ] = useLazyQuery<
    GetInterviewAvailableSlots,
    GetInterviewAvailableSlotsVariables
  >(GET_INTERVIEW_AVAILABLE_SLOTS, {
    fetchPolicy: 'cache-and-network',
  });

  const [
    getAvailableInterviewers,
    { loading: loadingAvailableInterviewers },
  ] = useLazyQuery<getAvailableInterviewers, getAvailableInterviewersVariables>(
    GET_AVAILABLE_INTERVIEWERS
  );

  const selectedDate = watch('selectedDate');
  const selectedTimeSlot = watch('selectedTimeSlot');
  const selectedDateRef = useCurrentMutableStateRef(selectedDate);

  const selectedMonthYear = useMemo(
    () => `${dayjs(selectedDate).month()}.${dayjs(selectedDate).year()}`,
    [selectedDate]
  );

  const handleGetAvailableTimeSlots = useCallback(async () => {
    const startEnd = getStartEndOfMonth(selectedDateRef.current);

    // Helper function to get all available interviewers and their slots
    const getAvailableInterviewersAndSlots = async () => {
      if (interviewId && !interviewData?.getInterview) {
        return;
      }
      const {
        data: interviewersData,
        error: interviewersError,
      } = await getAvailableInterviewers({
        variables: {
          input: {
            source:
              interviewData?.getInterview?.source?.type ||
              InterviewSourceType.UserRequest,
          },
        },
      });

      if (interviewersData?.getAvailableInterviewers) {
        await getAllAvailableScheduleSlots({
          variables: {
            input: {
              userIds: interviewersData?.getAvailableInterviewers.map(
                (interviewer) => interviewer.id
              ),
              dateRange: {
                from: startEnd.start.utc().toDate(),
                to: startEnd.end.utc().toDate(),
              },
            },
          },
        });
      } else {
        throw new Error(
          interviewersError?.message || 'No available interviewers found'
        );
      }
    };

    try {
      let interviewSlotsData;

      if (!interviewId) {
        if (!hostId) {
          // No interview - get slots from all available interviewers
          await getAvailableInterviewersAndSlots();
        } else {
          await getAvailableScheduleSlots({
            variables: {
              input: {
                userId: hostId,
                dateRange: {
                  from: startEnd.start.utc().toDate(),
                  to: startEnd.end.utc().toDate(),
                },
              },
            },
          });
        }
      } else {
        // We have an interview - check if we have a specific host
        if (hostId) {
          // Interview with specific host - get interview-specific slots for this host
          const result = await getInterviewAvailableSlots({
            variables: {
              input: {
                interviewId,
                dateRange: {
                  from: startEnd.start.utc().toDate(),
                  to: startEnd.end.utc().toDate(),
                },
              },
            },
          });
          interviewSlotsData = result.data;
        } else {
          // Interview but no specific host - get slots from all available interviewers
          await getAvailableInterviewersAndSlots();
        }
      }

      // Handle interviewer change callback for interview-specific slots
      if (
        interviewSlotsData?.getInterviewAvailableSlots.schedule &&
        onInterviewerChange
      ) {
        onInterviewerChange({
          firstName:
            interviewSlotsData.getInterviewAvailableSlots.schedule.store
              ?.firstName || '',
          lastName:
            interviewSlotsData.getInterviewAvailableSlots.schedule.store
              ?.lastName || '',
          storeDetails: {
            avatarURL:
              interviewSlotsData.getInterviewAvailableSlots.schedule.store
                .storeDetails?.avatarURL,
          },
          sports:
            interviewSlotsData.getInterviewAvailableSlots.schedule.store.sports,
          id: interviewSlotsData.getInterviewAvailableSlots.schedule.store.id,
        } as Interviewer);
      }
    } catch (error) {
      showToast({
        message: error?.message || 'An error occurred',
        type: 'error',
      });
    }
  }, [
    selectedDateRef,
    interviewId,
    hostId,
    interviewData,
    getAvailableInterviewers,
    getAllAvailableScheduleSlots,
    getAvailableScheduleSlots,
    getInterviewAvailableSlots,
    onInterviewerChange,
  ]);

  const handleSelectTimeSlotChange = (slot: CommonSlot) => {
    setValue('selectedTimeSlot', slot.startTs);
    setError('selectedTimeSlot', {
      type: 'manual',
      message: '',
    });

    if (slot.userId) {
      setValue('selectedUserId', slot.userId);
    }
  };

  useEffect(() => {
    if (selectedMonthYear) {
      handleGetAvailableTimeSlots().catch((e) => console.error(e));
    }
  }, [handleGetAvailableTimeSlots, selectedMonthYear]);

  useEffect(() => {
    const filteredSlots: CommonSlot[] =
      scheduleSlotsData?.getAvailableScheduleSlots?.slots ||
      allScheduleSlotsData?.getAllAvailableScheduleSlots?.slots ||
      interviewAvailableSlotsData?.getInterviewAvailableSlots?.slots ||
      [];
    const selectedDateFormatted = dayjs(selectedDate).format('YYYY-MM-DD');

    if (Array.isArray(filteredSlots)) {
      const filtered = filteredSlots.filter((item, index, self) => {
        const date = dayjs(item.startTs).format('YYYY-MM-DD');

        if (date !== selectedDateFormatted) {
          return false;
        }

        const isUniqueStartTs =
          self.findIndex((slot) => slot.startTs === item.startTs) === index;
        // Filter out slots that belong to the logged-in user (they can't book their own slots)
        // Only filter if loggedUserId is provided and not empty
        const isNotHostSlot = !loggedUserId || item.userId !== loggedUserId;

        return isUniqueStartTs && isNotHostSlot;
      });

      setFilteredTimeSlots(filtered);
    } else {
      setFilteredTimeSlots([]);
    }

    if (!dayjs(selectedDate).isSame(dayjs(selectedTimeSlot), 'day')) {
      setValue('selectedTimeSlot', '');
    }
  }, [
    selectedDate,
    scheduleSlotsData,
    interviewAvailableSlotsData,
    allScheduleSlotsData,
    selectedTimeSlot,
    setValue,
    loggedUserId,
  ]);

  useEffect(() => {
    if (autoJumpedRef.current) {
      return;
    }

    const activeResult =
      scheduleSlotsData?.getAvailableScheduleSlots ||
      allScheduleSlotsData?.getAllAvailableScheduleSlots ||
      interviewAvailableSlotsData?.getInterviewAvailableSlots;

    if (!activeResult) {
      return;
    }

    const slots: CommonSlot[] = activeResult.slots ?? [];
    const bookableSlots = slots.filter(
      (slot) => !loggedUserId || slot.userId !== loggedUserId
    );

    const hasSlotsInVisibleMonth = bookableSlots.some((slot) =>
      dayjs(slot.startTs).isSame(selectedDate, 'month')
    );

    if (hasSlotsInVisibleMonth) {
      return;
    }

    const alternativeMonth = activeResult.earliestAlternativeRange?.month;

    if (alternativeMonth) {
      autoJumpedRef.current = true;
      setValue('selectedDate', dayjs(alternativeMonth.from).toDate());
      return;
    }

    if (bookableSlots.length > 0) {
      const earliestSlot = bookableSlots.reduce((earliest, slot) =>
        dayjs(slot.startTs).isBefore(dayjs(earliest.startTs)) ? slot : earliest
      );
      autoJumpedRef.current = true;
      setValue('selectedDate', dayjs(earliestSlot.startTs).toDate());
    }
  }, [
    scheduleSlotsData,
    allScheduleSlotsData,
    interviewAvailableSlotsData,
    selectedDate,
    loggedUserId,
    setValue,
  ]);

  const excludedDaysWithoutSlots = useMemo(() => {
    return getExcludedDates(
      scheduleSlotsData?.getAvailableScheduleSlots?.slots ||
        allScheduleSlotsData?.getAllAvailableScheduleSlots?.slots ||
        interviewAvailableSlotsData?.getInterviewAvailableSlots?.slots ||
        [],
      selectedDateRef.current,
      loggedUserId as string
    );
  }, [
    selectedDateRef,
    scheduleSlotsData,
    interviewAvailableSlotsData,
    allScheduleSlotsData,
    loggedUserId,
  ]);

  const loading =
    loadingAvailableScheduleSlots ||
    loadingAllScheduleSlots ||
    loadingInterviewSlots ||
    loadingAvailableInterviewers;
  const slotStepMinutes =
    scheduleSlotsData?.getAvailableScheduleSlots.schedule?.slotStepMinutes ||
    30;

  const calendarWrapperRef = useRef<HTMLDivElement>(null);
  const [calendarHeight, setCalendarHeight] = useState<number | null>(null);

  // Sync right container height with calendar height
  useEffect(() => {
    const updateHeight = () => {
      if (calendarWrapperRef.current) {
        setCalendarHeight(calendarWrapperRef.current.clientHeight);
      }
    };

    // Initial height
    updateHeight();

    // Watch for calendar size changes (month changes, etc.)
    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    if (calendarWrapperRef.current) {
      resizeObserver.observe(calendarWrapperRef.current);
    }

    // Also update when month/year changes
    const timeoutId = setTimeout(updateHeight, 100);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [selectedMonthYear]);

  return (
    <form className={styles.root}>
      <div>
        <div className={styles.dateAndTimePickerWrapper}>
          <Controller
            control={control}
            name="selectedDate"
            render={({ onChange, onBlur, value }) => (
              <div ref={calendarWrapperRef}>
                <DatePicker
                  showMonthDropdown
                  showYearDropdown
                  timeIntervals={slotStepMinutes}
                  calendarClassName={styles.calendar}
                  onBlur={onBlur}
                  selected={value}
                  onChange={onChange}
                  onMonthChange={onChange}
                  onYearChange={onChange}
                  minDate={minCalendarDateRef.current}
                  placeholderText="MM/DD/YYYY"
                  dateFormat="MMMM d, yyyy"
                  excludeDates={excludedDaysWithoutSlots}
                  formatWeekDay={(name) => name.substring(0, 3)}
                  inline
                />
              </div>
            )}
          />

          <div
            className={styles.slotRoot}
            style={{
              height: isMobileScreen
                ? 'auto'
                : calendarHeight
                ? calendarHeight - 4
                : 'auto',
            }}
          >
            <Text variant="body1Regular16" className={styles.dateText}>
              {isMobileScreen
                ? 'Pick Time'
                : dayjs(selectedDate).format('dddd, MMMM, DD')}
            </Text>

            <div className={styles.timeSlotsContainer}>
              {loading ? (
                <div className={styles.loaderContainer}>
                  <Loader loading size="medium" />
                </div>
              ) : (
                <ul>
                  {filteredTimeSlots.length ? (
                    filteredTimeSlots.map((item) => {
                      // Proper UTC to local timezone conversion using dayjs plugins
                      const guestTimezone = dayjs.tz.guess();
                      const localDate = dayjs
                        .utc(item.startTs)
                        .tz(guestTimezone);
                      const formattedDate = localDate.format('h:mm A');
                      const isSelected = selectedTimeSlot === item.startTs;

                      return (
                        <li key={item.startTs}>
                          <button
                            type="button"
                            className={cn(styles.timeBtn, {
                              [styles.selectedTimeSlot]: isSelected,
                            })}
                            onClick={() => handleSelectTimeSlotChange(item)}
                          >
                            {formattedDate}
                          </button>
                        </li>
                      );
                    })
                  ) : (
                    <p className={styles.noDateTime}>
                      No time available on this date.
                    </p>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>

        {errors?.selectedTimeSlot?.message && (
          <Text variant="body1Regular16" color="error-default">
            {errors.selectedTimeSlot.message}
          </Text>
        )}
      </div>
      <TextArea
        ref={register}
        label="Interview notes"
        rows={isMobileScreen ? 4 : 3}
        name="description"
        placeholder="Your Host can ask you about any cool moments, upcoming events, new merch drops, or big announcements. Just let us know!"
        error={errors?.description?.message}
      />
    </form>
  );
};

export default CalendarStepForm;
